import React, { useEffect, useRef, useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { deleteBarcode } from "../../../graphql/mutations";
import { getInventorieInbound } from "../../../graphql/inventory/custom-queries";
import { getWorkOrderMobile } from "../../../graphql/workOrder/queriesLite";
import { adaptWorkOrderForCrewDetail } from "../../../graphql/workOrder/workOrderAdaptersLite";
import {
  getLocation,
  barcodeByInventoryByStatusByLocation,
} from "../../../graphql/queries";
import {
  createInventoryItem,
  updateInventorieInbound,
} from "../../../graphql/inventory/custom-mutations";
import { sanitizeInventory } from "../../../graphql/inventory/inventoryCRUD";
import { useTrackCarSubscriptionRefresh } from "../../../utils/useTrackCarSubscriptionRefresh";

import { useRedirect } from "react-admin";

import {
  Box,
  Button,
  CardActions,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  useMediaQuery,
  FormControl,
  FormLabel,
  FormGroup,
  Checkbox,
  Backdrop,
  CardContent,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import CheckBoxOutlineBlankOutlinedIcon from "@material-ui/icons/CheckBoxOutlineBlankOutlined";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";

import {
  InventoryStatus,
  UserType,
  WorkOrderState,
} from "../../../utils/constants";
import { useWorkOrderContext } from "./context";
import ErrorCard from "../../../components/ErrorCard";
import { NoteType, BarcodesStatus } from "../../../utils/constants";
import {
  getLocationFullName,
  isBulkArea,
  isRailShipmentInTrackArea,
  isTrack,
  setInventoryAisleList,
  sortByLocation,
} from "../../../utils/locationUtils";
import {
  getUnitName,
  isBulkShipment,
  isShippingContainer,
} from "../../../utils/loadTypeUtils";
import {
  updateExceptionQuantity,
  getSpottedTrackId,
} from "../../../utils/utils";
import { logError } from "../../../utils/appUtils";

import CrewCard from "../../../components/common/CrewCard";
import { CustomFieldsMobile } from "../../../components/common/CustomFields";
import { ExceptionIcon } from "../../../components/common/iconography";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";

import InboundInventoryItems from "./InboundInventoryItems";
import { InboundAisleMap } from "./InboundAisleMap";

const useStyles = makeStyles((theme) => ({
  actions: {
    display: "flex",
    justifyContent: "space-between",
  },
  avatar: {
    marginTop: theme.spacing(1),
  },
  progress: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 300,
  },
  exceptionContainer: {
    marginBottom: theme.spacing(2),
  },
  backdrop: {
    zIndex: 90001,
  },
}));

export const ShipmentItemCard = ({ history, match }) => {
  const {
    state = {},
    dispatch,
    getAisles,
    notify,
  } = useWorkOrderContext() ?? {};
  const {
    currentInventory,
    currentShipment,
    displayState,
    selectedOutbound,
    loading,
  } = state;
  const [invalidIdError, setInvalidIdError] = useState();
  const [note, setNote] = useState();
  const [isExceptionDialogOpen, setIsExceptionDialogOpen] = useState();
  const classes = useStyles();
  const redirect = useRedirect();
  const isComponentMounted = useRef(true);
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const sourceInboundShipmentId =
    currentShipment?.inboundShipmentId || currentShipment?.id;

  useEffect(() => {
    // Ensure we always land on the inventory view first when (re)entering
    // This prevents stale state (e.g., displayState === "MAP") from a previous session
    // causing an immediate jump to the aisle map before inventory is fetched
    dispatch({
      type: "SET_DISPLAY_STATE",
      payload: { displayState: "INVENTORY_ITEMS" },
    });
    getInventory(match.params.id);
    return () => (isComponentMounted.current = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useTrackCarSubscriptionRefresh({
    enabled: Boolean(currentShipment?.reloadYardId),
    label: "WorkOrderItemCard",
    shouldRefresh: (trackCar) => {
      if (!trackCar?.reloadYardId) return false;
      if (trackCar.reloadYardId !== currentShipment.reloadYardId) return false;
      if (
        sourceInboundShipmentId &&
        trackCar?.shipmentId &&
        trackCar.shipmentId !== sourceInboundShipmentId
      ) {
        return false;
      }
      return true;
    },
    onRefresh: (trackCar, eventName) => {
      if (
        eventName === "update" &&
        trackCar?.shipmentId === sourceInboundShipmentId &&
        trackCar?.trackId
      ) {
        dispatch({
          type: "UPDATE_SHIPMENT_LOCATION",
          payload: {
            shipmentId: trackCar.shipmentId,
            trackId: trackCar.trackId,
            locationFullName: trackCar.locationFullName,
          },
        });
      }
      getInventory(match.params.id, { showSpinner: false });
    },
  });

  const getInventory = async (id, { showSpinner = true } = {}) => {
    // if (!isComponentMounted.current || loading) return;
    // setLoading(true);
    if (showSpinner) {
      dispatch({ type: "SET_LOADING", payload: true });
    }
    try {
      const response = await API.graphql(
        graphqlOperation(getInventorieInbound, { id }),
      );
      const inventory = response.data.getInventorie;
      if (inventory) {
        let inboundShipment = currentShipment;
        if (!currentShipment?.id) {
          // Deep links / refresh enter here with no in-memory work order.
          // `inventory.workOrderId` is the canonical join key (schema:
          // Inventory.workOrderId -> WorkOrder). Using inboundShipmentId
          // here would return null and break the adapter.
          const workOrderId = inventory.workOrderId;
          if (!workOrderId) {
            throw new Error(
              `Inventory ${inventory.id} has no workOrderId; cannot resolve work order context.`,
            );
          }
          const response = await API.graphql(
            graphqlOperation(getWorkOrderMobile, { id: workOrderId }),
          );
          inboundShipment = adaptWorkOrderForCrewDetail(
            response.data.getWorkOrder,
          );
          dispatch({ type: "SET_CURRENT_SHIPPMENT", payload: inboundShipment });
        }

        if (isBulkOrContainer(inventory)) {
          // if bulk commodity stored on track, inventoryItem already placed in railcar.
          // if shipping container, only 1 inventory item.
          const quantityOnHand = inventory.inventoryItems?.items.reduce(
            (total, itm) => total + Number(itm.quantityOnHand || 0),
            0,
          );
          inventory.quantityReceived = quantityOnHand
            ? quantityOnHand
            : inventory.quantityAdvised;
        }
        if (
          inventory.inventoryItems.items.length === 0 &&
          isTrack(inventory.area)
        ) {
          if (getSpottedTrackId(inboundShipment)) {
            const inventoryItem = await doCreateInventoryItem(
              inventory,
              InventoryStatus.STORED,
              inboundShipment,
            );
            inventory.inventoryItems.items.push(inventoryItem);
          } else if (showSpinner) {
            // Only on a load the crew asked for. Background TrackCar refreshes
            // would otherwise repeat this warning for as long as the car sits
            // unspotted.
            notify(
              "Railcar is not spotted. Spot it in Track Management before storing inventory.",
              "warning",
            );
          }
        }

        sortByLocation(inventory.inventoryItems.items);
        await getAisles(inventory);
        dispatch({ type: "SET_CURRENT_INVENTORY", payload: inventory });
      } else {
        setInvalidIdError(`Inventory ID ${id} doesn't exist.`);
      }
    } catch (err) {
      logError("InventoryItemCard:getInventory", err, notify);
    } finally {
      // setLoading(false);
      if (showSpinner) {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    }
  };

  const isBulkOrContainer = (inventory) => {
    return (
      isBulkShipment(
        inventory.reloadYardCommodity?.commodity,
        inventory.area,
      ) || isShippingContainer(inventory.reloadYardCommodity?.commodity)
    );
  };

  const doCreateInventoryItem = async (
    currentInventory,
    status,
    shipment = currentShipment,
  ) => {
    debugger;
    const toCreateInventoryItem = {
      quantityOnHand: currentInventory.quantityAdvised,
    };
    toCreateInventoryItem.quantityCommitted = 0;
    toCreateInventoryItem.inboundShipmentId = sourceInboundShipmentId;
    toCreateInventoryItem.groups = currentInventory.groups;
    toCreateInventoryItem.accountId = currentInventory.accountId;
    toCreateInventoryItem.inventoryId = currentInventory.id;
    toCreateInventoryItem.reloadYardId = currentInventory.reloadYardId;
    toCreateInventoryItem.status = status;
    const trackId = getSpottedTrackId(shipment);
    if (!trackId) {
      throw new Error(
        `ShipmentItem: shipment ${shipment?.id} has no spottable trackId ` +
          `(trackCar.trackId=${JSON.stringify(shipment?.trackCar?.trackId)}) for track placement`,
      );
    }
    let response = await API.graphql(
      graphqlOperation(getLocation, { id: trackId }),
    );
    const location = response.data.getLocation;
    toCreateInventoryItem.locationId = location.id;
    toCreateInventoryItem.aisleId = location.aisleId;
    toCreateInventoryItem.zoneId = location.zoneId;
    toCreateInventoryItem.areaId = location.areaId;
    toCreateInventoryItem.locationFullName = getLocationFullName(location);
    toCreateInventoryItem.history = [
      {
        type: NoteType.SHIPMENT,
        text: `Railcar placed on track ${toCreateInventoryItem.locationFullName}`,
        date: new Date(),
        by: window.appUser.fullName,
      },
    ];
    toCreateInventoryItem.unloadedBy = window.appUser.fullName;
    toCreateInventoryItem.unloadedAt = new Date();
    response = await API.graphql(
      graphqlOperation(createInventoryItem, { input: toCreateInventoryItem }),
    );
    return response.data.createInventoryItem;
  };

  const saveInventory = async (inventory) => {
    debugger;
    let toUpdateInventory = { ...inventory };
    setInventoryAisleList(toUpdateInventory);
    updateExceptionQuantity(toUpdateInventory);

    toUpdateInventory = sanitizeInventory(toUpdateInventory);
    const response = await API.graphql(
      graphqlOperation(updateInventorieInbound, { input: toUpdateInventory }),
    );
    const updatedInventory = response.data.updateInventorie;
    debugger;
    dispatch({ type: "UPDATE_CURRENT_INVENTORY", payload: updatedInventory });
  };

  const setNextState = (currentInventory) => {
    currentInventory.receivedAt = currentShipment.placedDateTime
      ? currentShipment.placedDateTime
      : new Date();
    if (
      currentInventory.status === InventoryStatus.NEW ||
      currentInventory.status === InventoryStatus.IN_TRANSIT
    ) {
      currentInventory.status = InventoryStatus.ARRIVED;
    }
    if (currentInventory.status === InventoryStatus.ARRIVED) {
      currentInventory.status = InventoryStatus.UNLOADED;
    } else if (currentInventory.status === InventoryStatus.REVIEW) {
      if (
        isBulkArea(currentInventory.areaId) &&
        !currentShipment.containerDrop
      ) {
        currentInventory.status = InventoryStatus.AGGREGATED;
      } else currentInventory.status = InventoryStatus.STORED;
    }
  };

  // Check for barcodes that are in NEW status but not associated with any inventory item
  // For clean up purposes if a user entered barcodes but didn't save the inventory item,
  // those barcodes will be deleted, since they are not associated with any inventory item and just taking space in the database.
  // They are queried by inventoryId and status, we don't need the locationId here since we are searching for barcodes
  // in the entire inventory, not just in a specific location.
  const clearZombieBarcodes = async () => {
    try {
      const result = await API.graphql(
        graphqlOperation(barcodeByInventoryByStatusByLocation, {
          inventoryId: currentInventory.id,
          statusLocationId: {
            beginsWith: {
              status: BarcodesStatus.NEW,
            },
          },
          limit: 1000,
        }),
      );

      const newBarcodes =
        result.data.barcodeByInventoryByStatusByLocation.items;

      await Promise.all(
        newBarcodes.map(async (barcode) => {
          try {
            await API.graphql(
              graphqlOperation(deleteBarcode, {
                input: { id: barcode.id },
              }),
            );
          } catch (deleteErr) {
            logError(
              "ShipmentItemCard:clearZombieBarcodes:deleteBarcode",
              deleteErr,
              null,
              redirect,
            );
          }
        }),
      );
    } catch (error) {
      logError("ShipmentItemCard:clearZombieBarcodes", error, null, redirect);
    }
  };

  const rightButtonHandler = () => {
    if (currentInventory.status === InventoryStatus.UNLOADED) handleCancel();
    else handleComplete();
  };

  const handleComplete = async () => {
    debugger;
    dispatch({ type: "SET_LOADING", payload: true });
    const previousStatus = currentInventory.status;

    await clearZombieBarcodes();

    setNextState(currentInventory);
    if (note) {
      const newNote = {
        date: new Date(),
        by: window.appUser?.fullName,
        type: NoteType.GENERIC,
        text: note,
      };
      if (currentInventory.notes) currentInventory.notes.push(newNote);
      else currentInventory.notes = [newNote];
    }
    if (currentInventory.status !== previousStatus) {
      const newHistory = {
        type: NoteType.SHIPMENT,
        date: new Date(),
        by: window.appUser?.fullName,
        text: `Inventory saved | status: ${currentInventory.status} | quantity received: ${currentInventory.quantityReceived}`,
      };
      if (currentInventory.history) currentInventory.history.push(newHistory);
      else currentInventory.history = [newHistory];
    }

    try {
      await saveInventory(currentInventory);
      goToShipment();
    } catch (err) {
      logError(
        "processWorkOrders:WorkOrderItemCard:handleComplete",
        err,
        notify,
        redirect,
      );
    }
  };

  const goToShipment = () => {
    clear();
    history.replace("/processWorkOrders/" + currentShipment.id);
  };

  const handleSave = async () => {
    debugger;
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      if (note) {
        const newNote = {
          date: new Date(),
          by: window.appUser?.fullName,
          type: NoteType.GENERIC,
          text: note,
        };
        if (currentInventory.notes) currentInventory.notes.push(newNote);
        else currentInventory.notes = [newNote];
      }
      await saveInventory(currentInventory);
      goToShipment();
    } catch (err) {
      logError(
        "processWorkOrders:WorkOrderItemCard:handleSave",
        err,
        notify,
        redirect,
      );
    }
  };

  const isValidQuantity = (value) => {
    if (
      currentInventory.overShipment &&
      value > currentInventory.quantityAdvised
    )
      return true;
    if (
      currentInventory.underShipment &&
      value < currentInventory.quantityAdvised
    )
      return true;
    if (
      !currentInventory.overShipment &&
      !currentInventory.underShipment &&
      value === currentInventory.quantityAdvised
    )
      return true;
    return false;
  };

  const handleCancel = () => {
    if (displayState === "MAP") {
      dispatch({
        type: "SET_DISPLAY_STATE",
        payload: { displayState: "INVENTORY_ITEMS" },
      });
    } else {
      goToShipment();
    }
  };

  const clear = () => {
    dispatch({ type: "CLEAR_INVENTORY" });
  };

  const toggleChecked = (value) => {
    currentInventory.verificationRequired = value;
    dispatch({
      type: "SET_CURRENT_INVENTORY",
      payload: currentInventory,
    });
  };

  const getLeftButtonText = () => {
    let text = "Save";
    if (currentInventory.verificationRequired === "true") {
      text = "Request Approval";
    }
    return text;
  };

  const getRightButtonText = () => {
    let text = "Mark Stored";
    if (currentInventory.status === InventoryStatus.UNLOADED) {
      text = "Cancel";
    } else if (currentShipment?.state === WorkOrderState.PENDING_REVIEW)
      text = "Mark Complete";

    return text;
  };

  const isMarkStoredDisabled = () => {
    // Nothing can be stored into a railcar that is not spotted on a real track.
    if (
      isRailShipmentInTrackArea(currentInventory.area, currentShipment) &&
      !getSpottedTrackId(currentShipment)
    )
      return true;
    if (
      loading ||
      !currentInventory.inventoryItems?.items ||
      currentInventory.inventoryItems?.items.length === 0
    )
      return true;
    if (!isValidQuantity(currentInventory.quantityReceived)) return true;
    if (currentInventory.inventoryItems?.items.length > 0) {
      if (
        currentInventory.inventoryItems.items.find(
          (itm) =>
            itm.status !== InventoryStatus.UNLOADED &&
            itm.status !== InventoryStatus.STORED &&
            itm.status !== InventoryStatus.AGGREGATED,
        )
      )
        return true;
    }
    return currentInventory.verificationRequired === "true";
  };

  const refreshFn = async (refreshInventory) => {
    debugger;
    refreshInventory && (await getInventory(currentInventory.id));
    if (selectedOutbound)
      dispatch({ type: "SET_SELECTED_OUTBOUND", payload: null });
    dispatch({
      type: "SET_DISPLAY_STATE",
      payload: { displayState: "INVENTORY_ITEMS" },
    });
  };

  const handleSaveExceptions = (inventory) => {
    debugger;
    if (inventory.hasException) {
      if (!currentShipment.hasException) currentShipment.hasException = true;
      inventory.verificationRequired = "true";
    }
    saveInventory(inventory);
    dispatch({
      type: "UPDATE_CURRENT_SHIPMENT",
      payload: currentShipment,
    });
  };

  const getErrorMessage = () => {
    const hasQuantityReceived =
      currentInventory.inventoryItems?.items?.length > 0 &&
      !Number.isNaN(Number(currentInventory.quantityReceived));

    if (
      hasQuantityReceived &&
      !isValidQuantity(currentInventory.quantityReceived)
    ) {
      if (
        currentInventory.quantityReceived === currentInventory.quantityAdvised
      ) {
        if (currentInventory.overShipment)
          return "⚠️ Overshipment not applicable";
        if (currentInventory.underShipment)
          return "⚠️ Undershipment not applicable";
      }
      if (currentInventory.quantityReceived > currentInventory.quantityAdvised)
        return "⚠️ Set overshipment exception";
      return "⚠️ Set undershipment exception";
    } else {
      return "";
    }
  };

  const showQuantityAdvised = () => {
    if (
      currentInventory?.quantityAdvised &&
      (window.appUser.type !== UserType.CREW ||
        isBulkShipment(currentInventory?.reloadYardCommodity?.commodity))
    )
      return true;
    return false;
  };

  const getInputProps = () => {
    if (showQuantityAdvised())
      return {
        endAdornment: (
          <InputAdornment position="end">
            {`/ ${currentInventory.quantityAdvised} ${getUnitName(
              currentInventory.inventoryUOM,
            )}`}
          </InputAdornment>
        ),
      };
    return null;
  };

  if (invalidIdError) return <ErrorCard error={invalidIdError} />;

  return (
    <CrewCard
      title={currentInventory.productName}
      icon={<CheckBoxOutlineBlankOutlinedIcon />}
      subheader={accountNameRenderer(currentShipment)}
      action={
        <IconButton
          edge="start"
          onClick={() => {
            if (displayState === "MAP") {
              refreshFn(true);
            } else {
              handleCancel();
            }
          }}
        >
          <HighlightOffIcon fontSize="large" />
        </IconButton>
      }
      showDivider={false}
    >
      {displayState === "MAP" ? (
        <InboundAisleMap refreshFn={refreshFn} />
      ) : (
        <>
          <CardContent>
            <Backdrop className={classes.backdrop} open={loading}>
              <CircularProgress color="inherit" />
            </Backdrop>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography>
                  Lot number: {currentInventory.lotNumber}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>PO number: {currentInventory.poNumber}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>{`Area: ${currentInventory?.area?.name}`}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <Typography component="span">Zone:&nbsp;</Typography>
                  <FiberManualRecordIcon
                    style={{ color: currentInventory?.zone?.color }}
                  />
                  <Typography component="span">
                    {currentInventory?.zone?.name}
                  </Typography>
                </Box>
              </Grid>
              {currentInventory.productCode && (
                <Grid item xs={12} sm={6}>
                  <Typography>
                    Product code: {currentInventory.productCode}
                  </Typography>
                </Grid>
              )}
              {currentInventory.customFields && (
                <Grid item xs={12}>
                  <CustomFieldsMobile
                    recordFields={currentInventory?.customFields}
                    accountFields={
                      currentShipment.account.inventoryCustomFields
                    }
                    width={6}
                  />
                </Grid>
              )}
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      color={
                        currentInventory.verificationRequired === "true"
                          ? "secondary"
                          : "default"
                      }
                      checked={currentInventory.verificationRequired === "true"}
                      onChange={() =>
                        toggleChecked(
                          currentInventory.verificationRequired === "true"
                            ? "false"
                            : "true",
                        )
                      }
                      disabled={currentInventory.hasException}
                    />
                  }
                  label={xsDown ? "Approval" : "Request approval"}
                />
              </Grid>
              <Grid item xs={6}>
                <Box
                  display="flex"
                  width="100%"
                  justifyContent={xsDown ? "flex-end" : "flex-start"}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setIsExceptionDialogOpen(true)}
                    startIcon={
                      currentInventory.hasException ? <ExceptionIcon /> : null
                    }
                  >
                    {xsDown ? "Exceptions" : "Report Exception"}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={xsDown ? 0 : 2}>
                  <Grid item xs={12} sm={6} />
                  <Grid item xs={12} sm={6}>
                    <TextField
                      type="number"
                      name="quantityReceived"
                      label="Quantity Received"
                      id="quantity-received"
                      margin="dense"
                      variant="outlined"
                      value={
                        currentInventory.quantityReceived
                          ? currentInventory.quantityReceived
                          : "0"
                      }
                      error={
                        currentInventory.quantityReceived
                          ? !isValidQuantity(currentInventory.quantityReceived)
                          : false
                      }
                      helperText={getErrorMessage()}
                      fullWidth
                      disabled
                      InputProps={getInputProps()}
                    />
                  </Grid>
                </Grid>
              </Grid>
              {<InboundInventoryItems />}
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  id="notes"
                  label="Notes"
                  multiline
                  minRows={3}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  onChange={(evt) => setNote(evt.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>

          <CardActions className={classes.actions}>
            <Button size="large" onClick={handleSave} disabled={loading}>
              {getLeftButtonText()}
            </Button>
            <Button
              size="large"
              onClick={rightButtonHandler}
              disabled={isMarkStoredDisabled()}
              color="primary"
            >
              {getRightButtonText()}
            </Button>
          </CardActions>
        </>
      )}
      {isExceptionDialogOpen && (
        <ExceptionDialog
          currentShipment={currentShipment}
          currentInventory={currentInventory}
          handleSaveExceptions={handleSaveExceptions}
          classes={classes}
          isExceptionDialogOpen={isExceptionDialogOpen}
          setIsExceptionDialogOpen={setIsExceptionDialogOpen}
        ></ExceptionDialog>
      )}
    </CrewCard>
  );
};

const ExceptionDialog = ({
  currentShipment,
  currentInventory,
  handleSaveExceptions,
  isExceptionDialogOpen,
  setIsExceptionDialogOpen,
  classes,
}) => {
  const [inventory] = useState({ ...currentInventory });
  const [overShipment, setOverShipment] = useState(inventory.overShipment);
  const [overShipmentNote, setOverShipmentNote] = useState(
    inventory.overShipmentNote,
  );
  const [underShipment, setUnderShipment] = useState(inventory.underShipment);
  const [underShipmentNote, setUnderShipmentNote] = useState(
    inventory.underShipmentNote,
  );
  const [otherException, setOtherException] = useState(
    inventory.otherException,
  );
  const [otherExceptionNote, setOtherExceptionNote] = useState(
    inventory.otherExceptionNote,
  );
  const [otherExceptionNoteError, setOtherExceptionNoteError] = useState();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const handleSave = () => {
    if (!validate()) return;
    debugger;
    inventory.overShipment = overShipment;
    inventory.underShipment = underShipment;
    inventory.otherException = otherException;
    inventory.overShipmentNote = overShipmentNote;
    inventory.underShipmentNote = underShipmentNote;
    inventory.otherExceptionNote = otherExceptionNote;
    if (!overShipment) inventory.overShipmentQuantity = 0;
    if (!underShipment) inventory.underShipmentQuantity = 0;
    if (
      inventory.overShipment ||
      inventory.underShipment ||
      inventory.otherException
    ) {
      inventory.hasException = true;
      inventory.verificationRequired = "true";
    } else {
      inventory.hasException = false;
    }
    handleSaveExceptions(inventory);
    setIsExceptionDialogOpen(false);
  };

  const validate = () => {
    clear();
    let isValid = true;
    if (otherException) {
      isValid = isValid && otherExceptionNote;
      !otherExceptionNote &&
        setOtherExceptionNoteError("A note is required for other exceptions");
    }
    return isValid;
  };

  const handleChange = (evt) => {
    const type = evt.target.name;
    const value = evt.target.checked;
    debugger;
    if (type === "overShipment") {
      setOverShipment(value);
      if (value) {
        setUnderShipment(!value);
        setUnderShipmentNote("");
      } else {
        setOverShipmentNote("");
      }
    } else if (type === "underShipment") {
      setUnderShipment(value);
      if (value) {
        setOverShipment(!value);
        setOverShipmentNote("");
      } else {
        setUnderShipmentNote("");
      }
    } else {
      setOtherException(value);
      if (!value) {
        setOtherExceptionNote("");
        setOtherExceptionNoteError("");
      }
    }
  };

  const clear = () => {
    setOtherExceptionNoteError("");
  };

  return (
    <Dialog
      open={isExceptionDialogOpen}
      maxWidth={isSmall ? "xl" : "md"}
      fullWidth={true}
    >
      <Box
        component="span"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        m={2}
      >
        <Typography variant="h6">{currentShipment.accountName}</Typography>
        <CancelOutlinedIcon
          fontSize="large"
          onClick={() => setIsExceptionDialogOpen(false)}
        />
      </Box>
      <DialogContent>
        <FormControl component="fieldset" className={classes.formControl}>
          <FormLabel>Exception Type</FormLabel>
          <FormGroup row={!isSmall}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={overShipment}
                  onChange={handleChange}
                  name="overShipment"
                  disabled={
                    underShipment ||
                    (!overShipment &&
                      currentInventory.quantityReceived <=
                        currentInventory.quantityAdvised)
                  }
                />
              }
              label="Overshipment"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={underShipment}
                  onChange={handleChange}
                  name="underShipment"
                  disabled={
                    overShipment ||
                    (!underShipment &&
                      currentInventory.quantityReceived >=
                        currentInventory.quantityAdvised)
                  }
                />
              }
              label="Undershipment"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={otherException}
                  onChange={handleChange}
                  name="other"
                />
              }
              label="Other"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentInventory.isDamaged}
                  name="Damage"
                  disabled
                />
              }
              label="Is damaged"
            />
          </FormGroup>
        </FormControl>
        {overShipment && (
          <div className={classes.exceptionContainer}>
            <Typography>Overshipment</Typography>
            <TextField
              name="overShipmentNote"
              label="Notes"
              multiline
              minRows={3}
              margin="dense"
              variant="outlined"
              fullWidth
              value={overShipmentNote}
              onChange={(evt) => setOverShipmentNote(evt.target.value)}
            />
          </div>
        )}
        {underShipment && (
          <div className={classes.exceptionContainer}>
            <Typography>Undershipment</Typography>
            <TextField
              name="underShipmentNote"
              label="Notes"
              multiline
              minRows={3}
              margin="dense"
              variant="outlined"
              fullWidth
              value={underShipmentNote}
              onChange={(evt) => setUnderShipmentNote(evt.target.value)}
            />
          </div>
        )}
        {otherException && (
          <div className={classes.exceptionContainer}>
            <Typography>Other</Typography>
            <TextField
              name="otherNotes"
              label="Notes"
              multiline
              minRows={3}
              autoFocus
              error={otherExceptionNoteError}
              helperText={otherExceptionNoteError}
              margin="dense"
              variant="outlined"
              required
              fullWidth
              value={otherExceptionNote}
              onChange={(evt) => setOtherExceptionNote(evt.target.value)}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsExceptionDialogOpen(false)}>Cancel</Button>
        <Button type="submit" onClick={handleSave} color="primary">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
